import { createContext, useContext, useState, useCallback, useRef } from 'react'
import { supabase } from '../lib/supabase'

const AutonomousAgentContext = createContext(null)

// Maps a phase/section name to its sectionKey — used by AgentStatusPanel to link queue items to real phases
export function phaseNameToSectionKey(name) {
  const n = (name || '').toLowerCase()
  if (n.includes('pestel') || n.includes('f1') || n.includes('análisis macro') || n.includes('analisis macro')) return 'f1'
  if (n.includes('f2') || n.includes('industria')) return 'f2'
  if (n.includes('f3') || n.includes('mercados objetivo') || n.includes('priorización') || n.includes('priorizacion')) return 'f3'
  if (n.includes('unidad') || n.includes(' bu') || n.includes('bu ')) return 'bu'
  if (n.includes('e1') || (n.includes('análisis del mercado') && !n.includes('consumidor'))) return 'e1'
  if (n.includes('e2') || n.includes('consumidor')) return 'e2'
  if (n.includes('e3') || n.includes('competitiv')) return 'e3'
  if (n.includes('e4') || n.includes('interno')) return 'e4-1'
  if (n.includes('e5') || n.includes('estrategia por mercado')) return 'e5'
  return null
}

// ── Question definitions ──────────────────────────────────────────────────────
// Each question maps to a section in the NVC methodology.
// All questions require user input — the agent asks and waits for the user's response.
export const AUTONOMOUS_QUESTIONS = [
  {
    id: 'q-init',
    sectionKey: 'init',
    section: 'Inicio del proceso estratégico',
    priority: 1,
    emoji: '🏢',
    agentPrompt: `¡Hola! 👋 Vamos a construir el plan estratégico paso a paso.

Para comenzar, necesito lo básico:

**¿Cuál es el nombre de la empresa cliente y en qué industria opera?** Si ya tienes el país o países principales y algún contexto inicial (tamaño, antigüedad, misión), inclúyelo aquí — eso me permitirá avanzar más rápido.`,
    bannerText: '¿Cuál es el nombre y la industria de la empresa cliente?',
  },
  {
    id: 'q-f1',
    sectionKey: 'f1',
    section: 'F1 — Análisis Macro (PESTEL)',
    priority: 2,
    emoji: '🌍',
    agentPrompt: `Perfecto. Pasemos al **Análisis Macro (PESTEL)** — el entorno externo que rodea a la empresa.

Necesito tu perspectiva sobre el contexto actual. Cuéntame:

**¿Cuáles son los factores del entorno que más están impactando (o podrían impactar) al negocio?** Piensa en:
- **Político/Legal**: ¿Hay regulaciones nuevas, cambios de gobierno, restricciones comerciales?
- **Económico**: ¿Cómo está la economía local? ¿Inflación, tasas de interés, tipo de cambio?
- **Social**: ¿Qué cambios en hábitos de consumo o demografía están notando?
- **Tecnológico**: ¿Qué tecnologías están cambiando la industria o el negocio?
- **Ambiental**: ¿Hay presiones de sostenibilidad o regulaciones ambientales relevantes?

No necesitas cubrirlos todos — comparte lo que conoces y yo completo el análisis.`,
    bannerText: '¿Qué factores del entorno macro impactan más al negocio? (PESTEL)',
  },
  {
    id: 'q-f2',
    sectionKey: 'f2',
    section: 'F2 — Análisis de la Industria',
    priority: 3,
    emoji: '🏭',
    agentPrompt: `Ahora vamos al **Análisis de la Industria** — cómo funciona el sector donde compite la empresa.

Cuéntame desde tu conocimiento del mercado:

**¿Cómo describirías la dinámica competitiva de la industria?** Me ayuda saber:
- ¿Qué tan intensa es la competencia? ¿Hay muchos jugadores o pocos dominantes?
- ¿Es fácil para nuevas empresas entrar al mercado?
- ¿Los proveedores o los clientes tienen mucho poder de negociación?
- ¿Hay productos sustitutos que amenacen a la categoría?
- ¿La industria está creciendo, estable o en contracción?

Con esto construyo el análisis de las 5 Fuerzas de Porter y las tendencias del sector.`,
    bannerText: '¿Cómo es la dinámica competitiva de la industria? (5 Fuerzas)',
  },
  {
    id: 'q-f3',
    sectionKey: 'f3',
    section: 'F3 — Mercados Objetivo y Priorización',
    priority: 4,
    emoji: '🎯',
    agentPrompt: `Excelente, avancemos con los **mercados objetivo**.

Para definir dónde debe enfocarse la empresa estratégicamente necesito que me cuentes:

**¿En cuántos mercados o categorías de producto compite la empresa actualmente? Menciona cuáles son.** También, ¿hay nuevos mercados o geografías que estén considerando entrar en los próximos 2-3 años?

*Ejemplo: "Compite en snacks, bebidas y pasabocas. Están explorando entrar a Ecuador."*`,
    bannerText: '¿En cuántos mercados compite la empresa y cuáles son?',
  },
  {
    id: 'q-bu',
    sectionKey: 'bu',
    section: 'Definición de Unidades de Negocio',
    priority: 5,
    emoji: '🗂️',
    agentPrompt: `Perfecto. Ahora vamos a definir las **unidades de negocio** que estructurarán el análisis a nivel mercado.

**¿Cuántas unidades de negocio o categorías principales tiene la empresa? Por favor menciona el nombre de cada una.**

*Ejemplo: "3 unidades: (1) Snacks salados, (2) Bebidas naturales, (3) Pasabocas premium"*

Esto es clave para organizar el análisis por mercado (Etapas 1-5) de la metodología NVC.`,
    bannerText: '¿Cuántas unidades de negocio tiene la empresa y cuáles son sus nombres?',
  },
  {
    id: 'q-e1',
    sectionKey: 'e1',
    section: 'E1 — Análisis del Mercado (tamaño, CAGR, segmentos)',
    priority: 6,
    emoji: '📊',
    agentPrompt: `Ahora vamos al **Análisis del Mercado** — entender el tamaño y dinámica de la categoría donde compite la empresa.

Comparte lo que tengas disponible (no importa si no es exacto):

**¿Qué información tienes sobre el mercado en el que opera la empresa?** Por ejemplo:
- ¿Cuánto vale el mercado total (en ventas o volumen)? ¿Está creciendo?
- ¿En qué épocas del año se vende más o menos (estacionalidad)?
- ¿Cómo se divide el mercado internamente? (por precio: premium/económico, por canal: retail/canal tradicional/e-commerce, por ocasión de consumo)
- ¿Cuáles son los márgenes típicos del sector?

Si tienes estudios de mercado, reportes o datos internos, menciónalos — los incorporo al análisis.`,
    bannerText: '¿Qué información tienes sobre el tamaño y dinámica del mercado?',
  },
  {
    id: 'q-e2',
    sectionKey: 'e2',
    section: 'E2 — Análisis del Consumidor',
    priority: 7,
    emoji: '👥',
    agentPrompt: `Ahora vamos al **análisis del consumidor** — una de las secciones más importantes para la estrategia.

Para esto necesito información específica de la empresa que no puedo encontrar en internet:

**¿Quién es el consumidor típico de la empresa? Describe el perfil:** edad, nivel socioeconómico, qué valora al comprar, cómo decide entre marcas, y dónde compra habitualmente (canal físico o digital).

Si tienen investigaciones de consumidor propias, focus groups o datos de CRM, menciónalos — eso enriquecerá mucho el análisis.`,
    bannerText: '¿Quién es el consumidor típico de la empresa? (perfil, valores, proceso de compra)',
  },
  {
    id: 'q-e3',
    sectionKey: 'e3',
    section: 'E3 — Análisis Competitivo',
    priority: 8,
    emoji: '⚔️',
    agentPrompt: `Excelente. Pasemos al **análisis competitivo**.

Aunque puedo investigar competidores en internet, necesito tu perspectiva interna:

**¿Cuáles son los 3-5 principales competidores directos de la empresa?** Para cada uno, comparte:
- ¿Qué los diferencia?
- ¿Cuál es su posicionamiento percibido?
- ¿Tienes estimado de market share?

Y muy importante: **¿cuál es el competidor que más te preocupa y por qué?**`,
    bannerText: '¿Cuáles son los principales competidores y qué los diferencia?',
  },
  {
    id: 'q-e4-portafolio',
    sectionKey: 'e4-1',
    section: 'E4.1 — Análisis Interno: Portafolio y Variables Competitivas',
    priority: 9,
    emoji: '🔍',
    agentPrompt: `Ahora entramos al **análisis interno** — la mirada hacia adentro de la empresa.

Esta información es confidencial y solo tú la tienes:

**¿Cuál es el portafolio actual de productos o servicios de la empresa?** Describe:
- Principales productos y sub-marcas
- En qué canales vende (supermercados, tiendas, e-commerce, otros)
- Rango de precios y posicionamiento de precio
- Cómo se comunica la marca hoy (medios, mensaje central)

Sé tan detallado como puedas — más contexto = mejor diagnóstico.`,
    bannerText: '¿Cuál es el portafolio actual, canales de venta y cómo se comunica la marca?',
  },
  {
    id: 'q-e4-cap',
    sectionKey: 'e4-2',
    section: 'E4.2 — Capacidades Organizacionales',
    priority: 10,
    emoji: '⚙️',
    agentPrompt: `Sigamos con las **capacidades internas** de la empresa.

Sé directo y honesto — esta información es crítica para identificar las opciones estratégicas reales:

**¿Cuáles son las principales fortalezas y debilidades de la empresa en estas dimensiones?**
- **Equipo**: ¿Tienen el talento para ejecutar la estrategia?
- **Tecnología y datos**: ¿Tienen sistemas para tomar decisiones basadas en datos?
- **Capacidad financiera**: ¿Tienen acceso a capital para inversión?
- **Supply chain**: ¿La cadena de suministro es una ventaja o una limitación?
- **Cultura**: ¿La organización está lista para el cambio?`,
    bannerText: '¿Cuáles son las fortalezas y debilidades internas de la empresa?',
  },
  {
    id: 'q-e5',
    sectionKey: 'e5',
    section: 'E5 — Estrategia por Mercado',
    priority: 11,
    emoji: '🚀',
    agentPrompt: `Llegamos al **corazón del plan estratégico**: la estrategia por mercado. Esta es la respuesta más importante de todo el proceso.

**¿Cuál es la ambición estratégica de la empresa a 3-5 años?**

Específicamente:
- **¿Dónde quieren crecer?** (geografías, categorías, segmentos)
- **¿Cómo quieren ganar?** (por precio, innovación, distribución, marca, servicio)
- **¿Cuál es el diferenciador central** que quieren tener o fortalecer?
- **¿Qué resultados de negocio esperan?** (participación de mercado, ventas, rentabilidad)

No hay respuesta incorrecta — esta visión es el punto de partida para las recomendaciones estratégicas.`,
    bannerText: '¿Cuál es la ambición estratégica de la empresa a 3-5 años?',
  },
]

// ── Email helper ──────────────────────────────────────────────────────────────
async function sendAlertEmail(question, consultancyId, consultancyName) {
  try {
    // Get current user email
    const { data: { user } } = await supabase.auth.getUser()
    if (!user?.email) return

    const appUrl = window.location.origin
    await supabase.functions.invoke('send-alert-email', {
      body: {
        to: user.email,
        consultancyName,
        questionText: question.bannerText,
        questionEmoji: question.emoji,
        sectionName: question.section,
        projectId: consultancyId,
        questionId: question.id,
        appUrl,
      },
    })
  } catch (e) {
    // Email is best-effort — never block the main flow
    console.warn('Alert email failed (non-critical):', e)
  }
}

// ── Context Provider ──────────────────────────────────────────────────────────
export function AutonomousAgentProvider({ children }) {
  const [activeConsultancyId, setActiveConsultancyId] = useState(null)
  const [activeConsultancyName, setActiveConsultancyName] = useState('')
  const [queue, setQueue] = useState([])
  const [isVisible, setIsVisible] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false) // agent is auto-filling a section
  const queueRecordIdRef = useRef(null)
  // Track which question IDs we've already emailed to avoid duplicates
  const emailedQuestionsRef = useRef(new Set())

  // ── Determine which sections are already "done" based on project data ──────
  function getCompletedSectionKeys(projectData) {
    const completed = new Set()
    if (!projectData) return completed

    const { consultancy, phases } = projectData

    // If company name + some context exists, 'init' is done
    if (consultancy?.name && (consultancy?.industry || consultancy?.client_company)) {
      completed.add('init')
    }

    // Check phases completion (70%+ = done for our purposes)
    for (const phase of (phases || [])) {
      const secs = phase.sections || []
      const totalTasks = secs.reduce((a, s) => a + (s.tasks?.length || 0), 0)
      const doneTasks = secs.reduce((a, s) => a + (s.tasks?.filter(t => t.status === 'completed').length || 0), 0)
      const pct = totalTasks > 0 ? doneTasks / totalTasks : 0
      if (pct >= 0.7) {
        const key = phaseNameToKey(phase.name)
        if (key) completed.add(key)
      }
    }

    return completed
  }

  function phaseNameToKey(name) {
    const n = (name || '').toLowerCase()
    if (n.includes('pestel') || n.includes('f1') || n.includes('análisis macro')) return 'f1'
    if (n.includes('f2') || n.includes('industria')) return 'f2'
    if (n.includes('f3') || n.includes('mercados objetivo') || n.includes('priorización')) return 'f3'
    if (n.includes('unidad') || n.includes('bu')) return 'bu'
    if (n.includes('e1') || (n.includes('análisis del mercado') && !n.includes('e2'))) return 'e1'
    if (n.includes('e2') || n.includes('consumidor')) return 'e2'
    if (n.includes('e3') || n.includes('competitiv')) return 'e3'
    if (n.includes('e4') || n.includes('interno')) return 'e4-1'
    if (n.includes('e5') || n.includes('estrategia por mercado')) return 'e5'
    return null
  }

  // ── Initialize queue for a consultancy ────────────────────────────────────
  const initQueue = useCallback(async (consultancyId, consultancyName, projectData) => {
    // Try to load existing queue
    try {
      const { data: existing } = await supabase
        .from('ai_reports')
        .select('id, content')
        .eq('consultancy_id', consultancyId)
        .eq('report_type', 'agent_queue')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle()

      if (existing?.content) {
        const stored = JSON.parse(existing.content)
        // Check if it's for the same consultancy and not too old
        if (stored.consultancyId === consultancyId) {
          queueRecordIdRef.current = existing.id
          setQueue(stored.questions || [])
          setActiveConsultancyId(consultancyId)
          setActiveConsultancyName(consultancyName)
          const hasUnanswered = (stored.questions || []).some(q => !q.answered)
          setIsVisible(hasUnanswered)
          return
        }
      }
    } catch (e) {
      console.error('Error loading agent queue:', e)
    }

    // Build fresh queue based on project completion
    const completedKeys = getCompletedSectionKeys(projectData)
    const pendingQuestions = AUTONOMOUS_QUESTIONS
      .filter(q => !completedKeys.has(q.sectionKey))
      .map(q => ({ ...q, answered: false }))

    if (pendingQuestions.length === 0) return // All done!

    const queueData = {
      consultancyId,
      createdAt: new Date().toISOString(),
      questions: pendingQuestions,
    }

    try {
      const { data } = await supabase.from('ai_reports').insert({
        consultancy_id: consultancyId,
        entity_type: 'consultancy',
        entity_id: consultancyId,
        report_type: 'agent_queue',
        content: JSON.stringify(queueData),
        generated_by: null,
      }).select('id').single()
      if (data?.id) queueRecordIdRef.current = data.id
    } catch (e) {
      console.error('Error saving agent queue:', e)
    }

    setQueue(pendingQuestions)
    setActiveConsultancyId(consultancyId)
    setActiveConsultancyName(consultancyName)
    setIsVisible(true)

    // Send email for the first question (non-blocking)
    const firstQuestion = pendingQuestions[0]
    if (firstQuestion && !emailedQuestionsRef.current.has(firstQuestion.id)) {
      emailedQuestionsRef.current.add(firstQuestion.id)
      sendAlertEmail(firstQuestion, consultancyId, consultancyName)
    }
  }, [])

  // ── Mark current question as answered and advance ─────────────────────────
  const markCurrentAnswered = useCallback(async () => {
    const firstUnanswered = queue.find(q => !q.answered)
    if (!firstUnanswered) return

    const updated = queue.map(q =>
      q.id === firstUnanswered.id ? { ...q, answered: true } : q
    )
    setQueue(updated)

    // If we don't have the record ID cached, look it up in Supabase
    // (happens when user navigates directly to agent page without loading ConsultancyPage)
    let recordId = queueRecordIdRef.current
    if (!recordId && activeConsultancyId) {
      try {
        const { data } = await supabase
          .from('ai_reports')
          .select('id')
          .eq('consultancy_id', activeConsultancyId)
          .eq('report_type', 'agent_queue')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle()
        if (data?.id) {
          recordId = data.id
          queueRecordIdRef.current = data.id
        }
      } catch (e) {
        console.error('Error looking up agent queue record:', e)
      }
    }

    // Persist to Supabase
    if (recordId && activeConsultancyId) {
      try {
        await supabase.from('ai_reports')
          .update({
            content: JSON.stringify({
              consultancyId: activeConsultancyId,
              questions: updated,
              updatedAt: new Date().toISOString(),
            })
          })
          .eq('id', recordId)
      } catch (e) {
        console.error('Error updating agent queue:', e)
      }
    }

    // Check if there's a next question
    const nextUnanswered = updated.find(q => !q.answered)
    setIsVisible(!!nextUnanswered)

    // Send email notification for the next pending question
    if (nextUnanswered && !emailedQuestionsRef.current.has(nextUnanswered.id)) {
      emailedQuestionsRef.current.add(nextUnanswered.id)
      sendAlertEmail(nextUnanswered, activeConsultancyId, activeConsultancyName)
    }
  }, [queue, activeConsultancyId, activeConsultancyName])

  // ── Reset queue — clears persisted record so initQueue rebuilds from scratch ─
  const resetQueue = useCallback(async () => {
    if (queueRecordIdRef.current) {
      try {
        await supabase.from('ai_reports').delete().eq('id', queueRecordIdRef.current)
      } catch (e) {
        console.error('Error deleting agent queue:', e)
      }
    }
    queueRecordIdRef.current = null
    setQueue([])
    setIsVisible(false)
  }, [])

  const hideQueue = () => setIsVisible(false)
  const showQueue = () => {
    const hasUnanswered = queue.some(q => !q.answered)
    if (hasUnanswered) setIsVisible(true)
  }

  // ── Derived state ─────────────────────────────────────────────────────────
  const currentQuestion = queue.find(q => !q.answered) || null
  const answeredCount = queue.filter(q => q.answered).length
  const totalCount = queue.length
  const remainingCount = totalCount - answeredCount

  return (
    <AutonomousAgentContext.Provider value={{
      activeConsultancyId,
      activeConsultancyName,
      queue,
      currentQuestion,
      answeredCount,
      totalCount,
      remainingCount,
      isVisible,
      isProcessing,
      setIsProcessing,
      initQueue,
      markCurrentAnswered,
      resetQueue,
      hideQueue,
      showQueue,
    }}>
      {children}
    </AutonomousAgentContext.Provider>
  )
}

export function useAutonomousAgent() {
  const ctx = useContext(AutonomousAgentContext)
  if (!ctx) throw new Error('useAutonomousAgent must be used within AutonomousAgentProvider')
  return ctx
}
